import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiZap } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { PATHS } from '@/routes/paths';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/custom/header/header';
import { BottomBarInfantil } from '@/components/custom/bottom-bar-infantil';
import tiaUau from '@/assets/images/tia/TIA-UAU.png';
import Breadcrumbs from '@/components/custom/breadcrumbs/breadcrumbs';
import { TypewriterText } from '@/components/custom/typewriter-text';

/**
 * JoinPage - Entry point for students joining a live challenge.
 *
 * Reads an optional `code` from the URL search params (e.g. from a QR code).
 * If the code is present it auto-submits; otherwise the student types the
 * access code manually.
 */
const JoinPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession, subscribe } = useLiveChallenge();

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoJoinAttempted, setAutoJoinAttempted] = useState(false);

  const handleJoin = useCallback(
    async accessCode => {
      if (!accessCode || loading) return;

      setLoading(true);
      try {
        const response = await liveChallengesApi.join(accessCode);
        const sessionData = response?.data ?? response;

        setSession(sessionData);
        subscribe(sessionData.activation_id, 'student');
        navigate(`/desafio-ao-vivo/${sessionData.activation_id}/aguardando`);
      } catch (error) {
        const message = error?.response?.data?.error || error?.response?.data?.message || 'Código inválido ou desafio não encontrado.';
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [loading, navigate, setSession, subscribe]
  );

  useEffect(() => {
    const urlCode = searchParams.get('code');
    if (urlCode && !autoJoinAttempted) {
      setAutoJoinAttempted(true);
      setCode(urlCode);
      handleJoin(urlCode);
    }
  }, [searchParams, autoJoinAttempted, handleJoin]);

  const handleSubmit = e => {
    e.preventDefault();
    handleJoin(code.trim());
  };

  const playful = usePlayfulStudent();

  const joinHeaderBlock = (
    <header className="mb-6 flex flex-col items-center gap-3">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-dropshadow-md">
        <FiZap className="h-7 w-7" />
      </div>
      <h1 className="m-0 text-2xl font-extrabold text-foreground">Desafio ao Vivo</h1>
      <p className="m-0 text-center text-sm text-neutral">Digite o código de acesso para entrar na sala.</p>
    </header>
  );

  const joinCard = (
    <div className="w-full max-w-md">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-dropshadow-lg"
      >
        <label
          htmlFor="access-code"
          className="text-sm font-semibold text-foreground"
        >
          Código de acesso
        </label>
        <input
          id="access-code"
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          placeholder="ABC123"
          maxLength={10}
          autoFocus
          className="focus:ring-primary/20 w-full rounded-lg border-2 border-solid border-primary-lightest bg-bright p-4 text-center font-mono text-3xl font-bold uppercase tracking-[0.4em] text-primary-dark placeholder:font-normal placeholder:tracking-[0.2em] placeholder:text-neutral-light hover:border-primary-light focus:border-primary focus:bg-white focus:outline-none focus:ring-2"
        />

        {playful ? (
          <button
            type="submit"
            disabled={loading || !code.trim()}
            className="group/btn relative w-full overflow-hidden rounded-full py-3.5 text-base font-extrabold text-white transition-transform duration-100 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              background: 'linear-gradient(180deg, color-mix(in srgb, var(--color-primary) 88%, #fff) 0%, var(--color-primary) 100%)',
              boxShadow: '0 4px 0 color-mix(in srgb, var(--color-primary) 62%, #000), 0 6px 14px rgba(0,0,0,0.18)'
            }}
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.28) 0%, transparent 100%)' }}
            />
            <span className="relative">{loading ? 'Entrando...' : 'Entrar'}</span>
          </button>
        ) : (
          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            disabled={loading || !code.trim()}
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </Button>
        )}
      </form>

      <p className="mt-6 text-center text-xs text-neutral-light">Peça o código ao professor ou escaneie o QR code mostrado no telão.</p>

      {playful ? (
        <button
          type="button"
          onClick={() => navigate(PATHS.LIVE_CHALLENGE.MY_CHALLENGES)}
          className="mx-auto mt-4 block text-sm font-extrabold text-primary underline decoration-2 underline-offset-4 transition-opacity hover:opacity-80"
        >
          Ver meus desafios
        </button>
      ) : (
        <div className="mt-4 text-center">
          <Button
            type="button"
            variant="link"
            onClick={() => navigate(PATHS.LIVE_CHALLENGE.MY_CHALLENGES)}
          >
            Meus desafios
          </Button>
        </div>
      )}
    </div>
  );

  // Infantil: ganha o chrome (header + bottom bar) + banner da T.I.A explicando o desafio.
  if (playful) {
    return (
      <>
        <Header />
        <Breadcrumbs data={{ title: 'Avaliações e Questões', nav: [{ name: 'Desafio ao Vivo', isActive: true }] }} />
        <main className="min-h-screen bg-neutral-bright px-4 pb-36 pt-44">
          <div
            className="relative mx-auto mb-8 flex max-w-4xl items-center rounded-[24px] p-4 pl-[150px] shadow-card"
            style={{ background: 'var(--color-bg-lilac)' }}
          >
            <img
              src={tiaUau}
              alt="T.I.A"
              className="pointer-events-none absolute bottom-0 left-2 z-10 h-[180px] w-auto object-contain object-bottom"
            />
            <div className="relative z-10 max-w-[700px] rounded-[16px] rounded-bl-none bg-white p-4 shadow-card">
              <span
                className="block text-xs font-extrabold"
                style={{ color: 'var(--color-secondary-dark)' }}
              >
                T.I.A
              </span>
              <p
                className="mb-0 text-sm font-bold leading-relaxed"
                style={{ color: '#222731' }}
              >
                <TypewriterText text={'Bora pro Desafio ao Vivo? ⚡ Quanto mais rápido e certo, mais pontos. Boa sorte! 🚀'} />
              </p>
            </div>
          </div>
          <div className="flex justify-center">{joinCard}</div>
        </main>
        <BottomBarInfantil />
      </>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bright px-4 py-12">
      {joinHeaderBlock}
      {joinCard}
    </div>
  );
};

export default JoinPage;
